import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ArrowRight,
  Sparkles,
  Layers,
  TrendingUp,
  Server,
} from "lucide-react";
import { toast } from "react-toastify";
import { login_success } from "../../actions/user";
import ApiClient from "../api/apiClient";
import loader from "../loader";
import methodModel from "../methods";
import FormControl from "../../components/common/FormControl";
import { getTenantInfo } from "../../utils/domain";

export default function Login() {
  const history = useNavigate();
  const dispatch = useDispatch();
  const WebDetails = useSelector((state) => state.logo);
  const tenantInfo = getTenantInfo();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(null);

  // Fetch public tenant branding if on a tenant subdomain
  useEffect(() => {
    if (tenantInfo.isTenant && tenantInfo.slug) {
      ApiClient.get(`tenants/public/${tenantInfo.slug}`, {}, "", true)
        .then((res) => {
          if (res?.success && (res?.data || res?.tenant)) {
            setTenantBranding(res.data || res.tenant);
          }
        })
        .catch(() => {});
    }
  }, [tenantInfo.isTenant, tenantInfo.slug]);

  // Check remembered credentials and URL message parameters on mount
  useEffect(() => {
    try {
      const savedRemember = localStorage.getItem("remember");
      if (savedRemember) {
        const parsed = JSON.parse(savedRemember);
        if (parsed?.email) {
          setUsername(parsed.email);
          setRemember(true);
        }
      }
    } catch (err) {
      console.error("Failed to parse remembered credentials", err);
    }

    const message = methodModel.getPrams("message");
    if (message) {
      toast.success(message);
    }
  }, []);

  // Store auth data in localStorage
  const storeAuthData = (data) => {
    if (data?.access_token) {
      localStorage.setItem("token", data.access_token);
    }
    const userId = data?._id || data?.id;
    if (userId) {
      localStorage.setItem("_id", userId);
    }
  };

  // Dispatch login success and redirect according to role
  const handleExistingUserLogin = (data) => {
    dispatch(
      login_success({
        ...data,
      })
    );
    loader(false);
    setLoading(false);

    // Auto-detect role from backend response and route accordingly
    const roleName = String(
      data?.role?.name ||
      data?.role ||
      data?.roleType ||
      data?.role_id ||
      ""
    ).toLowerCase();

    if (
      roleName.includes("tenant") ||
      data?.role?.name === "Tenant-Admin" ||
      data?.isTenantAdmin
    ) {
      history("/tenant/dashboard");
    } else if (
      roleName.includes("admin") ||
      tenantInfo.isTenant ||
      data?.permissions?.length > 0
    ) {
      history("/dashboard");
    } else {
      history("/products");
    }
  };

  // Set login details in cookie and Redux
  const setLogin = async (userData2) => {
    const userdata = {
      ...userData2,
      id: userData2?._id || userData2?.id,
      _id: userData2?._id || userData2?.id,
    };
    loader(true);
    storeAuthData(userdata);

    if (methodModel?.CookieUtil?.setCookieDay) {
      methodModel.CookieUtil.setCookieDay(
        "loggedInUser",
        JSON.stringify({
          id: userdata?.id,
        }),
        7
      );
    }

    // Attempt fetching admin profile if this is a tenant admin account
    try {
      const profileRes = await ApiClient.get("user/admin-profile", {}, "", true);
      if (profileRes?.success && profileRes?.data) {
        const mergedData = {
          ...userdata,
          ...profileRes.data,
          roleType: profileRes.data?.roleType || userdata?.roleType || "tenant_admin",
          isTenantAdmin: true,
        };
        localStorage.setItem("logedInUser", JSON.stringify(mergedData));
        handleExistingUserLogin(mergedData);
        return;
      }
    } catch (e) {
      // Profile fetch optional; proceed with userdata
    }

    localStorage.setItem("logedInUser", JSON.stringify(userdata));
    handleExistingUserLogin(userdata);
  };

  // Real-time Email Validation Handler matching Super Admin portal
  const handleEmailChange = (val) => {
    const raw = typeof val === "object" && val?.target !== undefined ? val.target.value : val;
    const str = String(raw || "");
    setUsername(str);

    const trimmed = str.trim();
    if (!trimmed) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter your email address.",
      }));
    } else {
      const check = methodModel.validateEmail(trimmed);
      setErrors((prev) => ({
        ...prev,
        email: check.success ? "" : check.message,
      }));
    }
  };

  const handleEmailBlur = () => {
    const trimmed = String(username || "").trim();
    if (!trimmed) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter your email address.",
      }));
    } else {
      const check = methodModel.validateEmail(trimmed);
      setErrors((prev) => ({
        ...prev,
        email: check.success ? "" : check.message,
      }));
    }
  };

  // Real-time Password Validation Handler matching Super Admin portal
  const handlePasswordChange = (val) => {
    const raw = typeof val === "object" && val?.target !== undefined ? val.target.value : val;
    const str = String(raw || "");
    setPassword(str);

    if (!str) {
      setErrors((prev) => ({
        ...prev,
        password: "Please enter your password.",
      }));
    } else {
      const check = methodModel.validateLoginPassword(str);
      setErrors((prev) => ({
        ...prev,
        password: check.success ? "" : check.message,
      }));
    }
  };

  const handlePasswordBlur = () => {
    if (!password) {
      setErrors((prev) => ({
        ...prev,
        password: "Please enter your password.",
      }));
    } else {
      const check = methodModel.validateLoginPassword(password);
      setErrors((prev) => ({
        ...prev,
        password: check.success ? "" : check.message,
      }));
    }
  };


  // Handle direct single-step login with simultaneous field validations
  const handleSubmit = (e) => {
    e.preventDefault();
    const emailTrimmed = (username || "").trim();
    let newErrors = { email: "", password: "" };
    let hasError = false;

    // Validate email
    const emailValidation = methodModel.validateEmail(emailTrimmed);
    if (!emailValidation.success) {
      newErrors.email = emailValidation.message;
      hasError = true;
    }

    // Validate password simultaneously
    const passwordValidation = methodModel.validateLoginPassword(password);
    if (!passwordValidation.success) {
      newErrors.password = passwordValidation.message;
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({ email: "", password: "" });

    const data = {
      email: emailTrimmed,
      password: password,
    };

    localStorage.setItem("SignUpEmail", data.email);
    loader(true);
    setLoading(true);

    const handleApiError = (msg) => {
      loader(false);
      setLoading(false);
      if (!msg) return;

      const lower = String(msg).toLowerCase();
      if (
        lower.includes("email") ||
        lower.includes("user") ||
        lower.includes("account")
      ) {
        setErrors({ email: msg, password: "" });
      } else if (lower.includes("password")) {
        setErrors({ email: "", password: msg });
      } else {
        setErrors({ email: msg, password: "" });
      }
    };

    // Primary latest API: user/admin/sign-in
    ApiClient.post("user/admin/sign-in", data, "", true, handleApiError)
      .then((res) => {
        if (res?.success) {
          loader(false);
          setLoading(false);
          if (remember) {
            localStorage.setItem(
              "remember",
              JSON.stringify({ email: emailTrimmed, remember: true })
            );
          } else {
            localStorage.removeItem("remember");
          }
          setLogin(res.data);
        } else {
          // If admin/sign-in does not recognize customer, attempt user/login
          ApiClient.post("user/login", data, "", true, handleApiError)
            .then((customerRes) => {
              loader(false);
              setLoading(false);
              if (customerRes?.success) {
                if (remember) {
                  localStorage.setItem(
                    "remember",
                    JSON.stringify({ email: emailTrimmed, remember: true })
                  );
                } else {
                  localStorage.removeItem("remember");
                }
                setLogin(customerRes.data);
              } else {
                const apiMsg =
                  customerRes?.error?.message ||
                  customerRes?.message ||
                  res?.error?.message ||
                  res?.message;
                if (apiMsg) handleApiError(apiMsg);
              }
            })
            .catch(() => {
              loader(false);
              setLoading(false);
              const apiMsg = res?.error?.message || res?.message;
              if (apiMsg) handleApiError(apiMsg);
            });
        }
      })
      .catch((err) => {
        // Fallback attempt to user/login
        ApiClient.post("user/login", data, "", true, handleApiError)
          .then((customerRes) => {
            loader(false);
            setLoading(false);
            if (customerRes?.success) {
              if (remember) {
                localStorage.setItem(
                  "remember",
                  JSON.stringify({ email: emailTrimmed, remember: true })
                );
              } else {
                localStorage.removeItem("remember");
              }
              setLogin(customerRes.data);
            } else {
              const errDetail =
                err?.response?.data?.error?.message ||
                err?.response?.data?.message ||
                err?.message;
              if (errDetail) handleApiError(errDetail);
            }
          })
          .catch(() => {
            loader(false);
            setLoading(false);
            const errDetail =
              err?.response?.data?.error?.message ||
              err?.response?.data?.message ||
              err?.message;
            if (errDetail) handleApiError(errDetail);
          });
      });
  };

  const logoSrc = WebDetails?.logo || "/assets/img/logo.png";
  const displayLogo =
    tenantBranding?.logo ||
    tenantBranding?.logo_url ||
    tenantBranding?.fileUrl ||
    logoSrc;
  const displayTitle =
    tenantBranding?.portal_title ||
    tenantBranding?.company_name ||
    (tenantInfo.isTenant
      ? `${tenantInfo.slug.toUpperCase()} Portal`
      : "Charles S. Smith & Associates");

  return (
    <>
      <div id="routerDivlogin" className="hidden"></div>
      <div className="h-screen w-full flex flex-col lg:flex-row bg-white lg:bg-[#081325] overflow-hidden antialiased selection:bg-[#115B9D] selection:text-white">
        {/* ======================================================== */}
        {/* LEFT BRAND & PLATFORM SHOWCASE PANEL (Visible on lg+) */}
        {/* ======================================================== */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[52%] h-full relative flex-col justify-between p-6 xl:p-10 2xl:p-12 overflow-hidden bg-gradient-to-br from-[#061224] via-[#0B2545] to-[#115B9D] text-white select-none">
          {/* Ambient Glow Orbs */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/3 w-80 h-80 bg-[#115B9D]/30 rounded-full blur-3xl pointer-events-none" />

          {/* Subtle Grid Pattern Overlay */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />

          {/* Top Branding Bar */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/15 shadow-xl">
              <img
                src={displayLogo}
                alt={displayTitle}
                className="h-9 w-auto object-contain bg-white rounded-lg p-1 shadow-sm"
              />
              <div className="leading-tight">
                <span className="block text-[10px] uppercase font-bold tracking-widest text-sky-300">
                  {tenantInfo.isTenant ? "Tenant Storefront" : "E-Commerce Suite"}
                </span>
                <span className="block text-xs font-semibold text-white tracking-tight">
                  {displayTitle}
                </span>
              </div>
            </div>

            {/* Live Status Pill */}
            <div className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Systems Operational
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-4 xl:py-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 border border-sky-300/30 text-sky-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-sky-300" />
              {tenantInfo.isTenant
                ? `${tenantInfo.slug.toUpperCase()} BUSINESS & STORE PORTAL`
                : "BUSINESS & CLIENT PORTAL"}
            </div>

            <h1 className="text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Unified Commerce For{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-sky-200 to-white">
                Beverage & Dispensing Equipment
              </span>
            </h1>

            <p className="mt-2.5 text-xs xl:text-sm text-sky-100 leading-relaxed max-w-lg font-normal">
              Manage your wholesale equipment orders, replacement parts, shipments, and business operations with enterprise-grade reliability.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-sky-400/20 flex items-center justify-center text-sky-300 mb-2">
                  <Layers className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Equipment & Parts
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Wholesale dispensers, pumps, parts & filtration.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-indigo-400/20 flex items-center justify-center text-indigo-300 mb-2">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Orders & Shipments
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Real-time tracking, documents & logistics updates.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-emerald-400/20 flex items-center justify-center text-emerald-300 mb-2">
                  <Server className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">
                  Business Management
                </h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Oversee store catalog, quotes, invoices & accounts.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Trust & Heritage Footer */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-sky-100">
            <span className="font-semibold text-white">
              Serving the Americas Since 1976
            </span>
            <span className="flex items-center gap-1.5 text-sky-200 font-medium">
              <Lock className="w-3.5 h-3.5 text-sky-300" /> 256-Bit SSL Encrypted
            </span>
          </div>
        </div>

        {/* ======================================================== */}
        {/* RIGHT LOGIN FORM PANEL (Seamless Clean Canvas) */}
        {/* ======================================================== */}
        <div className="w-full lg:w-1/2 xl:w-[48%] h-full flex items-center justify-center px-6 py-6 sm:px-10 sm:py-8 lg:px-12 xl:px-16 bg-white overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[400px] my-auto py-2">
            {/* Header with Logo */}
            <div className="text-center mb-5 sm:mb-6">
              <img
                src={displayLogo}
                alt={displayTitle}
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                Welcome Back
              </h2>
              <p className="text-xs text-slate-700 mt-1 leading-relaxed font-medium">
                {tenantInfo.isTenant
                  ? `Enter your credentials to access ${displayTitle}.`
                  : "Enter your credentials to access your store, orders, and account."}
              </p>
            </div>

            {/* Authentication Form with noValidate */}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-3.5 sm:space-y-4"
            >
              {/* Email Field using FormControl */}
              <FormControl
                type="text"
                name="email"
                id="userEmail"
                label="Email Address"
                required={true}
                placeholder="name@company.com"
                value={username}
                error={errors.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                autoComplete="username"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
              />

              {/* Password Field using FormControl with showEye */}
              <FormControl
                type="password"
                name="password"
                id="userPassword"
                label="Password"
                required={true}
                showEye={true}
                placeholder="Enter your password"
                value={password}
                error={errors.password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                autoComplete="current-password"
                divClass="space-y-1"
                labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
              />


              {/* Interactive Remember Me & Forgot Password Row */}
              <div className="flex items-center justify-between gap-2 pt-0.5">
                <label className="flex items-center gap-2 cursor-pointer select-none group">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      onClick={() => setRemember(!remember)}
                      className={`w-4 h-4 rounded border-[1.5px] flex items-center justify-center transition-colors cursor-pointer ${
                        remember
                          ? "bg-[#115B9D] border-[#115B9D]"
                          : "bg-white border-slate-700 hover:border-[#115B9D]"
                      }`}
                    >
                      {remember ? (
                        <svg
                          className="w-3 h-3 text-white pointer-events-none"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </div>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-900 group-hover:text-black transition-colors">
                    Remember me
                  </span>
                </label>

                <Link
                  to="/forgotpassword"
                  className="text-xs sm:text-sm font-bold text-[#115B9D] hover:text-[#0b4377] hover:underline transition-colors whitespace-nowrap"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Submit Action Button */}
              <div className="pt-1.5">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  <span>{loading ? "Signing in..." : "Sign In"}</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>

              {/* Customer Sign Up Option */}
              <div className="text-center pt-2">
                <span className="text-xs sm:text-sm text-slate-600">
                  Don't have an account?{" "}
                </span>
                <Link
                  to="/signup"
                  className="text-xs sm:text-sm font-bold text-[#115B9D] hover:text-[#0b4377] hover:underline transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            </form>

            {/* Security & Support Footer */}
            <div className="mt-5 pt-3.5 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-[#115B9D]" /> 256-Bit SSL Encrypted
              </span>
              <span className="font-semibold text-slate-800">
                Secure Portal Access
              </span>
            </div>

            {/* Copyright Note */}
            <p className="text-center text-[11px] text-slate-600 mt-2 font-medium">
              &copy; {new Date().getFullYear()} Charles S. Smith & Associates. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
